import CurrencyLogo from 'components/CurrencyLogo'
import { ManageButtonFlex } from 'components/PositionListItem/styled'
import { ROUTES } from 'config/routes'
import { StyledInternalLink } from 'theme'
import { PortfolioFarmPosition } from 'types/portfolio'
import { formatDollarAmount } from 'utils/portfolio/formatDollar'
import { getFarmPositionTotalValue } from 'utils/portfolio/getFarmPositionTotalValue'

import { FarmRewardsDisplay } from './FarmRewardsDisplay'
import { FarmSubRow } from './FarmSubRow'
import { PortfolioConnectorLines } from './PortfolioConnectorLines'
import { PortfolioMobileActionsFooter } from './PortfolioMobileActionsFooter'
import { PortfolioManageButton } from './PortfolioRowPrimitives'
import { PortfolioSchemaRow } from './PortfolioSchemaRow'
import { TokenBalanceDisplay } from './TokenBalanceDisplay'

interface CompactFarmRowProps {
  position: PortfolioFarmPosition
  onClaimSuccess?: (providerKey: 'kodiak' | 'bgt') => void
}

export function CompactFarmRow({ position, onClaimSuccess }: CompactFarmRowProps) {
  const { farm, formattedStakedAmount, apr, isSingleFarm, formattedWalletBalance, underlyingBalances } = position

  const stakingToken = farm.stakingToken
  const vault = farm.vault as
    | {
        id?: string
        token0?: { symbol: string; price?: number; decimals?: number }
        token1?: { symbol: string; price?: number; decimals?: number }
      }
    | null
    | undefined
  const totalValue = getFarmPositionTotalValue(position)

  const hasExpandableContent = true
  const manageRoute = isSingleFarm ? ROUTES.farms.single(farm.id) : ROUTES.islands.detail(vault?.id || farm.id, farm.id)

  const renderUnderlyingBalances = () => {
    if (isSingleFarm) {
      const staked = Number(formattedStakedAmount || 0)
      const available = Number(formattedWalletBalance || 0)
      const totalAmount = staked + available
      const totalUsd = (staked + available) * (stakingToken.price || 0)

      if (!totalAmount) {
        return <div className="text-kodiak-text-secondary text-right text-xs">—</div>
      }
      return (
        <div className="text-right">
          <TokenBalanceDisplay amount={totalAmount} symbol={stakingToken.symbol} usdValue={totalUsd} />
        </div>
      )
    }

    if (!underlyingBalances || !vault?.token0 || !vault?.token1) {
      return <div className="text-kodiak-text-secondary text-right text-xs">—</div>
    }

    const token0Amount = parseFloat(underlyingBalances.token0AmountFormatted || '0')
    const token1Amount = parseFloat(underlyingBalances.token1AmountFormatted || '0')
    const token0Value = token0Amount * (vault?.token0?.price || 0)
    const token1Value = token1Amount * (vault?.token1?.price || 0)

    return (
      <div className="text-right space-y-0.5">
        <TokenBalanceDisplay amount={token0Amount} symbol={vault?.token0?.symbol || ''} usdValue={token0Value} />
        <TokenBalanceDisplay amount={token1Amount} symbol={vault?.token1?.symbol || ''} usdValue={token1Value} />
      </div>
    )
  }

  return (
    <PortfolioSchemaRow
      hasExpandableContent={hasExpandableContent}
      desktopClassName="grid-cols-6 items-center py-3 px-6 gap-4"
      identity={{
        leading: <CurrencyLogo logoURI={stakingToken.logoUri} symbol={stakingToken.symbol} size="32px" />,
        title: stakingToken.symbol,
        subtitle: stakingToken.name,
      }}
      mobile={{
        value: (
          <div className="text-kodiak-text-primary font-semibold text-sm sm:text-base">
            {formatDollarAmount(totalValue)}
          </div>
        ),
        leftClassName: 'flex items-center space-x-3 min-w-0',
        valueRowClassName: 'flex items-center space-x-2',
      }}
      desktop={{
        showExpandIcon: true,
        subtitleClassName: 'text-xs text-kodiak-text-secondary',
        columns: (
          <>
            <div>{renderUnderlyingBalances()}</div>

            <div className="text-right">
              <div className="text-kodiak-text-primary text-xs">{apr.toFixed(2)}%</div>
            </div>

            <div className="text-kodiak-text-primary font-semibold text-right text-base">
              {formatDollarAmount(totalValue)}
            </div>

            <div className="text-right">
              <FarmRewardsDisplay farm={farm} className="text-sm" />
            </div>

            <ManageButtonFlex style={{ justifyContent: 'flex-end' }}>
              <PortfolioManageButton
                as={StyledInternalLink}
                to={manageRoute}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                Manage
              </PortfolioManageButton>
            </ManageButtonFlex>
          </>
        ),
      }}
      renderExpanded={() => (
        <div className="bg-kodiak-bg-module relative">
          <div className="relative">
            <PortfolioConnectorLines isFirst isLast />
            <FarmSubRow position={position} isLast onClaimSuccess={onClaimSuccess} />
          </div>

          <PortfolioMobileActionsFooter>
            <PortfolioManageButton
              as={StyledInternalLink}
              to={manageRoute}
              style={{ width: '100%' }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              Manage
            </PortfolioManageButton>
          </PortfolioMobileActionsFooter>
        </div>
      )}
    />
  )
}
